import { create } from 'zustand';
import type { Papel } from '@remoto/shared';
import { esvaziarCache, executarLimpezasDaSaida } from './cache';

export interface UsuarioLogado {
  id: string;
  email: string;
  nome: string;
  papel: Papel;
}

/**
 * Por que a pessoa foi deslogada sem pedir — hoje, só a substituição do login
 * único. Explícito de propósito (decisão registrada no plano da identidade):
 * "sua conta entrou em outro aparelho" evita o chamado de "o painel me
 * deslogou do nada" e desencoraja senha compartilhada.
 */
export type MotivoSaida = 'substituida';

interface EstadoSessao {
  /** O access token vive só em memória: storage seria alcançável por XSS. */
  acesso: string | null;
  expiraEm: number | null;
  usuario: UsuarioLogado | null;
  /** Preenchido quando a saída não foi escolha da pessoa; `entrar` limpa. */
  motivoSaida: MotivoSaida | null;
  entrar: (acesso: string, expiraEm: string, usuario: UsuarioLogado) => void;
  sair: (motivo?: MotivoSaida) => void;
  precisaRenovar: () => boolean;
}

const MARGEM_MS = 60_000;

export const usarSessao = create<EstadoSessao>((set, get) => ({
  acesso: null,
  expiraEm: null,
  usuario: null,
  motivoSaida: null,
  /*
    O cache morre junto com a sessão, nos DOIS lados da troca — o porquê de
    cada um está em `cache.ts`. O resumo: sem isto, entrar com outra conta
    desenhava a lista da conta anterior até o refetch voltar, mostrando
    conversas que o escopo do servidor nega. O servidor filtrava certo; quem
    mostrava dado velho era o cliente.
  */
  entrar: (acesso, expiraEm, usuario) => {
    esvaziarCache();
    set({ acesso, expiraEm: new Date(expiraEm).getTime(), usuario, motivoSaida: null });
  },
  sair: (motivo) => {
    esvaziarCache();
    // O que vive fora do cache (envios de anexo em andamento) morre junto.
    executarLimpezasDaSaida();
    set({ acesso: null, expiraEm: null, usuario: null, motivoSaida: motivo ?? null });
  },
  precisaRenovar: () => {
    const { acesso, expiraEm } = get();
    if (!acesso || !expiraEm) return true;
    return expiraEm - Date.now() < MARGEM_MS;
  },
}));
